import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuthorization } from 'core/context';
import { useToast } from 'core/toast';
import { getApplicant } from 'core/api';
import { Terms, CreditCheck, Summary, UserInfo } from './Steps';
import { Spinner } from 'view/components/layout';
import { Container } from './CreditPull.styles';
import { Card } from 'view/components/common';

enum UnderwritingSteps
{
    TERMS,
    INFO,
    CREDIT_CHECK,
    SUMMARY
}

export const CreditPull: React.FunctionComponent = () => {
    const history = useHistory();
    const authState = useAuthorization();
    const [ loading, setIsLoading ] = useState(true);
    const [ initialLoad, setInitialLoad ] = useState(true);
    const [ currentStep, setCurrentStep ] = useState<UnderwritingSteps>(UnderwritingSteps.TERMS);
    const [ hasConsumerReport, setHasConsumerReport ] = useState<boolean>(false);
    const { setToastMessage } = useToast();

    const stepNavigator = useCallback((increment : boolean) => {
        return () => {
            if (increment) setCurrentStep(s =>  s + 1);
            else setCurrentStep(s => s - 1);
        };
    }, [ currentStep, setCurrentStep ]);

    useEffect(() => {
        if (!initialLoad) {
            if (currentStep < UnderwritingSteps.TERMS || currentStep > UnderwritingSteps.SUMMARY) {
                history.push('/dashboard');
            } else {
                setIsLoading(false);
            }   
        }
    }, [ initialLoad, currentStep, history, setIsLoading ]);

    useEffect(() => {
        let isMounted = true;
        let fetchSucceeded = false;
        let fetchTimedOut = false;

        (async () => {
            const { applicant } = await getApplicant(authState);

            if (!fetchTimedOut && isMounted) {
                // Only applicant requested for 'credit check' can complete this step
                if (applicant?.status !== 'c') history.push('/dashboard');

                fetchSucceeded = true;
                setHasConsumerReport(applicant?.hasConsumerReport ?? false);
                setIsLoading(false);
                setInitialLoad(false);
            }
        })();

        // Notify & redirect the user if fetching their application takes longer than 60s
        const timeoutId = setTimeout(() => {
            if (!fetchSucceeded) {
                fetchTimedOut = true;
                setToastMessage();
                history.push('/dashboard');
            }
        }, 1000 * 60);

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, [ setToastMessage, setIsLoading, setInitialLoad ]);

    return (
        <Container isLoading={loading || initialLoad}>
            <Card height={620}>
                {loading ? (
                    <Spinner css='height: inherit; margin-top: 175px;' />
                ) : currentStep === UnderwritingSteps.TERMS ? (
                    <Terms stepNavigator={stepNavigator} />
                ) : currentStep === UnderwritingSteps.INFO ? (
                    <UserInfo stepNavigator={stepNavigator} />
                ) : currentStep === UnderwritingSteps.CREDIT_CHECK ? (
                    <CreditCheck
                        hasConsumerReport={hasConsumerReport}
                        stepNavigator={stepNavigator}
                    />
                ) : (
                    <Summary stepNavigator={stepNavigator} />
                )}
            </Card>
        </Container>
    );
};
